import * as React from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

function unescapeMarkdown(text) {
  //esline-disable-next-line
  return text.replace(/\\([\\`*_{}[\]()#+\-!])/g, "$1");
}

function renderInline(text, keyPrefix) {
  const nodes = [];
  const tokenRegex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match;
  let index = 0;

  while ((match = tokenRegex.exec(text))) {
    const plainText = text.slice(lastIndex, match.index);
    if (plainText) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-plain-${index}`}>
          {unescapeMarkdown(plainText)}
        </React.Fragment>
      );
      index += 1;
    }

    const token = match[0];
    const isBold = token.startsWith("**");
    const content = isBold ? token.slice(2, -2) : token.slice(1, -1);

    nodes.push(
      <Box
        key={`${keyPrefix}-token-${index}`}
        component="span"
        sx={{
          fontWeight: isBold ? 700 : undefined,
          fontStyle: isBold ? undefined : "italic",
        }}
      >
        {unescapeMarkdown(content)}
      </Box>
    );
    index += 1;
    lastIndex = match.index + token.length;
  }

  const remainder = text.slice(lastIndex);
  if (remainder) {
    nodes.push(
      <React.Fragment key={`${keyPrefix}-remainder-${index}`}>
        {unescapeMarkdown(remainder)}
      </React.Fragment>
    );
  }

  return nodes;
}

function parseMarkdown(markdown) {
  const lines = (markdown || "").split(/\r?\n/);
  const blocks = [];
  let paragraphLines = [];
  let list = null;

  function flushParagraph() {
    if (paragraphLines.length === 0) {
      return;
    }

    blocks.push({
      type: "paragraph",
      text: paragraphLines.join(" "),
    });
    paragraphLines = [];
  }

  function flushList() {
    if (!list || list.items.length === 0) {
      list = null;
      return;
    }

    blocks.push(list);
    list = null;
  }

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      return;
    }

    const imageMatch = trimmed.match(/^!\[(.*)\]\((.+)\)$/);
    if (imageMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "image",
        alt: imageMatch[1],
        src: imageMatch[2],
      });
      return;
    }

    const listMatch = trimmed.match(/^(-|\d+\.)\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      const ordered = listMatch[1] !== "-";

      if (!list || list.type !== "list" || list.ordered !== ordered) {
        flushList();
        list = {
          type: "list",
          ordered,
          items: [],
        };
      }

      list.items.push(listMatch[2]);
      return;
    }

    flushList();
    paragraphLines.push(trimmed);
  });

  flushParagraph();
  flushList();

  return blocks;
}

function headingVariant(level) {
  if (level <= 2) {
    return "h6";
  }

  if (level === 3) {
    return "subtitle1";
  }

  return "subtitle2";
}

export default function MarkdownContent({ markdown }) {
  const blocks = parseMarkdown(markdown);

  return (
    <Stack spacing={1.5}>
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          return (
            <Typography
              key={`block-${index}`}
              variant={headingVariant(block.level)}
              fontWeight={600}
            >
              {renderInline(block.text, `block-${index}`)}
            </Typography>
          );
        }

        if (block.type === "image") {
          return (
            <Box
              key={`block-${index}`}
              component="img"
              src={block.src}
              alt={block.alt || "Help illustration"}
              loading="lazy"
              sx={{
                width: "100%",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            />
          );
        }

        if (block.type === "list") {
          return (
            <List
              key={`block-${index}`}
              disablePadding
              sx={{ listStyleType: block.ordered ? "decimal" : "disc", pl: 3 }}
            >
              {block.items.map((item, itemIndex) => (
                <ListItem
                  key={`block-${index}-item-${itemIndex}`}
                  disablePadding
                  sx={{ display: "list-item", py: 0.25 }}
                >
                  <ListItemText
                    primaryTypographyProps={{ variant: "body2" }}
                    primary={renderInline(
                      item,
                      `block-${index}-item-${itemIndex}`
                    )}
                  />
                </ListItem>
              ))}
            </List>
          );
        }

        return (
          <Typography key={`block-${index}`} variant="body2">
            {renderInline(block.text, `block-${index}`)}
          </Typography>
        );
      })}
    </Stack>
  );
}
